import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Badge from "@/components/base/Badge";
import DramaCard from "@/components/base/DramaCard";
import { Breadcrumbs, EmptyState } from "@/components/feature/PageShell";
import { categories } from "@/mocks/categories";
import { findDrama } from "@/mocks/dramas";
import { specials } from "@/mocks/specials";
import { useBrowseHistory } from "@/hooks/useLocalCollections";
import { usePageMeta } from "@/hooks/usePageMeta";
import { formatDate, getRelated } from "@/utils/catalog";
import ActionShare from "./components/ActionShare";
import LocalRail from "./components/LocalRail";

export default function DetailPage() {
  const { id = "" } = useParams();
  const drama = findDrama(id);
  const { pushEntry } = useBrowseHistory();

  useEffect(() => {
    if (drama) {
      pushEntry({ id: drama.id, title: drama.title, cover: drama.cover });
      window.scrollTo({ top: 0 });
    }
  }, [drama, pushEntry]);

  usePageMeta({
    title: drama ? `${drama.title}详情 - 日本伦理剧` : "条目未找到 - 日本伦理剧",
    description: drama
      ? `${drama.title}（${drama.year}，${drama.region}）${drama.tagline}导演${drama.director}，共${drama.episodes}集。${drama.summary}`
      : "该条目可能已下架或链接有误。",
    keywords: drama
      ? `${drama.title},日本伦理剧,${drama.types.join(",")},${drama.director}`
      : "日本伦理剧",
  });

  if (!drama) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="container-page py-20">
          <EmptyState
            icon="ri-film-line"
            title="没有找到这部剧目"
            desc="它可能已被归档，或者链接中的编号有误。你可以回到首页，从最新更新中继续浏览。"
            action={
              <Link to="/" className="rounded-md bg-primary-900 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-800">
                返回首页
              </Link>
            }
          />
        </main>
        <SiteFooter />
      </div>
    );
  }

  const category = categories.find((item) => item.key === drama.category);
  const related = getRelated(drama, 3);
  const linkedSpecials = specials.filter((item) => drama.specialIds.includes(item.id));

  const infoRows = [
    { label: "导演", value: drama.director },
    { label: "主演", value: drama.cast.join(" / ") },
    { label: "类型", value: drama.types.join(" / ") },
    { label: "首播时间", value: formatDate(drama.premiere) },
    { label: "集数", value: `${drama.episodes} 集 · 单集约 ${drama.runtime} 分钟` },
    { label: "播出平台", value: drama.broadcaster },
    { label: "地区", value: drama.region },
    { label: "编辑评分", value: `${drama.rating.toFixed(1)} / 10` },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <section className="relative w-full overflow-hidden bg-primary-950">
          <div className="absolute inset-0">
            <img
              src={drama.wide}
              alt={`${drama.title} 剧照`}
              title={`${drama.title} 日本伦理剧详情`}
              className="h-full w-full object-cover object-top opacity-45"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-primary-950/40" />
          </div>
          <div className="container-page relative pb-10 pt-6 md:pb-14 md:pt-8">
            <div className="[&_a]:text-primary-200 [&_span]:text-primary-300">
              <Breadcrumbs
                items={[
                  { label: "首页", to: "/" },
                  { label: "日本伦理剧", to: "/category/ethics-drama" },
                  { label: category ? category.name : "作品", to: category ? `/category/${category.key}` : undefined },
                  { label: drama.title },
                ]}
              />
            </div>

            <div className="mt-8 grid gap-8 md:grid-cols-12">
              <div className="md:col-span-4 lg:col-span-3">
                <div className="aspect-[3/4] w-full max-w-[280px] overflow-hidden rounded-lg border border-background-200/20 bg-primary-900">
                  <img
                    src={drama.cover}
                    alt={`${drama.title} 高清封面`}
                    title={`${drama.title} ${drama.types[0]}`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="md:col-span-8 lg:col-span-9">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-sm bg-accent-500 px-2 py-1 font-label text-[10px] font-semibold tracking-[0.16em] text-primary-950">
                    {drama.year}
                  </span>
                  {drama.types.map((type) => (
                    <span
                      key={type}
                      className="rounded-full border border-background-200/30 px-2.5 py-0.5 text-[11px] text-primary-100"
                    >
                      {type}
                    </span>
                  ))}
                </div>
                <h1 className="mt-4 font-heading text-[28px] font-semibold leading-tight text-background-50 md:text-[40px]">
                  {drama.title}
                </h1>
                <p className="mt-1.5 font-label text-xs tracking-[0.12em] text-primary-300">{drama.originalTitle}</p>
                <p className="mt-4 max-w-2xl font-heading text-sm leading-relaxed text-accent-200 md:text-base">
                  「{drama.tagline}」
                </p>

                <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3">
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-heading text-3xl font-semibold text-background-50">
                      {drama.rating.toFixed(1)}
                    </span>
                    <span className="text-xs text-primary-300">/ 10 编辑评分</span>
                  </div>
                  <div className="text-xs text-primary-200">
                    {drama.episodes} 集 · {drama.region} · {formatDate(drama.premiere)} 首播
                  </div>
                </div>

                <div className="mt-6">
                  <ActionShare id={drama.id} title={drama.title} />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="container-page py-10 md:py-14">
          <div className="grid gap-10 lg:grid-cols-12">
            <article className="lg:col-span-8">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
                <h2 className="font-heading text-xl font-semibold text-foreground-950">基本信息</h2>
                <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                  {infoRows.map((row) => (
                    <div key={row.label} className="flex gap-3 border-b border-background-200 pb-2.5 last:border-0">
                      <dt className="w-16 shrink-0 text-xs text-foreground-500">{row.label}</dt>
                      <dd className="text-sm text-foreground-900">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
                <div className="flex items-center gap-2">
                  <span className="h-4 w-1 rounded-full bg-accent-500" />
                  <h2 className="font-heading text-xl font-semibold text-foreground-950">剧情简介</h2>
                </div>
                <p className="mt-4 text-[15px] font-medium leading-loose text-foreground-900">{drama.summary}</p>
                <div className="mt-5 space-y-4">
                  {drama.intro.map((paragraph, index) => (
                    <p key={index} className="text-sm leading-[1.9] text-foreground-700">
                      {paragraph}
                    </p>
                  ))}
                </div>

                <div className="mt-6 rounded-md border border-accent-200 bg-accent-50 p-4">
                  <p className="rule-label text-accent-800">编辑点评</p>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-800">{drama.editorNote}</p>
                </div>

                <div className="mt-6 flex flex-wrap gap-2">
                  {drama.tags.map((tag) => (
                    <Link key={tag} to={`/search?q=${encodeURIComponent(tag)}`}>
                      <Badge tone="secondary" size="md">
                        # {tag}
                      </Badge>
                    </Link>
                  ))}
                </div>
              </div>

              {linkedSpecials.length > 0 ? (
                <div className="mt-6 rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-7">
                  <h2 className="font-heading text-xl font-semibold text-foreground-950">所属专题</h2>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {linkedSpecials.map((item) => (
                      <Link
                        key={item.id}
                        to={`/special/${item.id}`}
                        className="group flex gap-3 rounded-md border border-background-200 bg-background-50 p-3 transition-colors hover:border-accent-300"
                      >
                        <div className="h-16 w-24 shrink-0 overflow-hidden rounded bg-background-200">
                          <img
                            src={item.cover}
                            alt={`${item.title} 专题主图`}
                            title={item.title}
                            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[11px] text-accent-700">{item.kicker}</p>
                          <p className="mt-1 line-clamp-2 text-sm font-medium text-foreground-900 group-hover:text-primary-800">
                            {item.title}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="mt-8">
                <div className="flex items-end justify-between">
                  <h2 className="font-heading text-xl font-semibold text-foreground-950">相关作品推荐</h2>
                  {category ? (
                    <Link
                      to={`/category/${category.key}`}
                      className="text-xs text-foreground-500 hover:text-accent-700"
                    >
                      更多{category.name} <i className="ri-arrow-right-line" />
                    </Link>
                  ) : null}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-3">
                  {related.map((item) => (
                    <DramaCard key={item.id} drama={item} />
                  ))}
                </div>
              </div>

              <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100/50 p-4">
                <p className="text-[11px] leading-relaxed text-foreground-500">
                  本页所有剧名、人物、评分与播出信息均为虚构演示数据，不涉及任何真实影视作品的版权内容，本站亦不提供播放或下载功能。
                </p>
              </div>
            </article>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24">
                <div className="mb-4 rounded-lg border border-background-200 bg-background-50 p-5">
                  <p className="rule-label text-foreground-500">条目速览</p>
                  <ul className="mt-3 space-y-2 text-xs text-foreground-700">
                    <li className="flex items-center justify-between">
                      <span className="text-foreground-500">题材分类</span>
                      <Link to={`/category/${drama.category}`} className="text-accent-700 hover:underline">
                        {category?.name ?? drama.category}
                      </Link>
                    </li>
                    <li className="flex items-center justify-between">
                      <span className="text-foreground-500">最近更新</span>
                      <span>{formatDate(drama.updatedAt)}</span>
                    </li>
                    <li className="flex items-center justify-between">
                      <span className="text-foreground-500">站内热度</span>
                      <span>{drama.hot} / 100</span>
                    </li>
                    <li className="flex items-center justify-between">
                      <span className="text-foreground-500">口碑区间</span>
                      <span>{drama.rating >= 9 ? "编辑部年度必看" : drama.rating >= 8.5 ? "值得一看" : "可作参考"}</span>
                    </li>
                  </ul>
                </div>
                <LocalRail currentId={drama.id} />
              </div>
            </aside>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}